
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Prestations', path: '/prestations' },
    { name: 'Véhicules', path: '/vehicules' },
    { name: 'CleanPass', path: '/cleanpass' },
    { name: 'Services', path: '/services-additionnels' },
    { name: 'Entreprises', path: '/entreprises' },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-dark-bg shadow-lg shadow-black/20' : 'bg-dark-bg/80 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/d9accc10-e356-4e90-b05d-65db94bbda2f.png"
              alt="LOUATI AUTOCENTER"
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive(link.path)
                    ? 'text-primary bg-primary/10'
                    : 'text-gray-300 hover:text-primary hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Side — Contact + CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            <a
              href="tel:+41796840588"
              className="flex items-center space-x-2 text-gray-400 hover:text-primary transition-colors"
            >
              <i className="ri-phone-line text-base"></i>
              <span className="text-sm font-medium whitespace-nowrap">+41 79 684 05 88</span>
            </a>
            <a
              href="https://wa.me/41796840588"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center text-green-500 hover:text-green-400 transition-colors"
            >
              <i className="ri-whatsapp-line text-xl"></i>
            </a>
            <Link
              to="/reservation"
              className="px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
            >
              Réserver
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-primary touch-manipulation active:scale-95 transition-transform"
            aria-label="Menu"
          >
            <i className={`text-3xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-dark-card border-t border-dark-border shadow-xl">
          <div className="flex flex-col p-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-lg min-h-[44px] flex items-center text-sm font-medium transition-colors touch-manipulation ${
                  isActive(link.path)
                    ? 'text-primary bg-primary/10'
                    : 'text-gray-300 hover:text-primary hover:bg-dark-bg'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}

            <Link
              to="/reservation"
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-center whitespace-nowrap cursor-pointer min-h-[44px] flex items-center justify-center font-semibold mt-2 touch-manipulation"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Réserver
            </Link>

            <div className="flex gap-2 pt-2 border-t border-dark-border mt-2">
              <a
                href="tel:+41796840588"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-dark-bg text-primary rounded-lg hover:bg-dark-border transition-colors min-h-[44px] touch-manipulation"
              >
                <i className="ri-phone-line text-xl"></i>
                <span className="text-sm font-medium">Appeler</span>
              </a>
              <a
                href="https://wa.me/41796840588"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] touch-manipulation"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span className="text-sm font-medium">WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
